import React, {useRef, useCallback, useEffect, useMemo, useState} from 'react';
import styles from './ImageGallery.module.scss';
import gsap from "gsap";
import {isMobileDevice} from "utils/Device";
import {staticWallsData} from "components/homePanels/e_contest/imageGallery/WallsStatic";

const ImageGallery = (props) => {

    const colorArray = useMemo(()=>['68,92,108', '242,221,207', '171,185,208', '162,65,51', '177,177,177'], []);

    const pointerDownTarget = useRef(0); // Target for pointer. If down, value is 1, else value is 0
    const pointerStart = useRef({x:0, y:0}); // Useful variables to keep track of the pointer
    const pointerDiffStart = useRef({x:0, y:0}); // Useful variables to keep track of the pointer

    const gridSize = 50;
    const gridMin = useMemo(()=>window.innerWidth > 768 ? 6 : 4,[]);
    const imagePadding = 20;

    const diffX = useRef(0);
    const diffY = useRef(0);
    const width = useRef(window.innerWidth);
    const height = useRef(window.innerHeight);
    const centerX = useRef(0);
    const centerY = useRef(0);
    const scrollCoords = useRef({x: 0, y: 0});
    const scrollTimeout = useRef();

    const widthRest = useRef();
    const heightRest = useRef();

    const uniforms = useRef({
        uResolution: {x:width.current, y:height.current},
        uPointerDiff: {x:0, y:0},
        uPointerDown: pointerDownTarget.current
    })

    const app = useRef()
    const container = useRef();
    const rects = useRef();
    const posts = useRef([]);
    const postUrls = useRef({});
    const [gridWrapper, setGridWrapper] = useState();

    const shuffle = useCallback((array)=>{
        let currentIndex = array.length, temporaryValue, randomIndex;

        // While there remain elements to shuffle...
        while (0 !== currentIndex) {

            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            // And swap it with the current element.
            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }

        return array;
    }, []);

    const submissions = useRef(shuffle(staticWallsData));

    /** Calculate grid dimensions PRIOR to first render **/

    const calculateGrid = useCallback((gridSize, gridColumns, gridRows, gridMin)=>{

        let newRects = [];
        let currentRects = [{ x: 0, y: 0, w: gridColumns, h: gridRows }];

        const randomInRange = (min, max) => {
            return Math.floor(Math.random() * (max - min + 1)) + min
        }

        const splitCurrentRect = () => {
            if (currentRects.length) {
                const currentRect = currentRects.shift()
                const cutVertical = currentRect.w > currentRect.h
                const cutSide = cutVertical ? currentRect.w : currentRect.h
                const cutSize = cutVertical ? 'w' : 'h'
                const cutAxis = cutVertical ? 'x' : 'y'
                if (cutSide > gridMin * 2) {
                    const rect1Size = randomInRange(gridMin, cutSide - gridMin)
                    const rect1 = Object.assign({}, currentRect, { [cutSize]: rect1Size })
                    const rect2 = Object.assign({}, currentRect, { [cutAxis]: currentRect[cutAxis] + rect1Size, [cutSize]: currentRect[cutSize] - rect1Size })
                    currentRects.push(rect1, rect2)
                }
                else {
                    newRects.push(currentRect)
                    splitCurrentRect()
                }
            }
        }

        // Call `splitCurrentRect` until there is no more rectangles on the list
        // Then return the list of rectangles
        const generateRects =  () => {
            while (currentRects.length) {
                splitCurrentRect()
            }
            return newRects
        }

        return {
            generateRects: generateRects
        }

    }, [])

    /** Initialize grid with new rectangles **/

    const initializeGrid = useCallback(()=>{

        // Variables and settings for grid
        let gridColumnsCount, gridRowsCount, gridColumns, gridRows, grid

        // Initialize the random grid layout
        // Getting columns
        gridColumnsCount = Math.ceil(width.current / gridSize)

        // Getting rows
        gridRowsCount = Math.ceil(height.current / gridSize)

        // Make the grid 5 times bigger than viewport
        gridColumns = gridColumnsCount * (window.innerWidth > 768 ? 3 : 5)
        gridRows = gridRowsCount * (window.innerWidth > 768 ? 3 : 5)

        // Create a new Grid instance with our settings
        grid = calculateGrid(gridSize, gridColumns, gridRows, gridMin)

        // Calculate the center position for the grid in the viewport
        widthRest.current = Math.ceil(gridColumnsCount * gridSize - width.current)
        heightRest.current = Math.ceil(gridRowsCount * gridSize - height.current)
        centerX.current = (gridColumns * gridSize / 2) - (gridColumnsCount * gridSize / 2)
        centerY.current = (gridRows * gridSize / 2) - (gridRowsCount * gridSize / 2)

        rects.current = grid.generateRects();

        let newGridWrapper = []

        rects.current.forEach((rect,rectIndex) => {

            let x = rect.x * gridSize;
            let y = rect.y * gridSize;

            let color = colorArray[Math.floor(Math.random() * colorArray.length)];

            let deets = (
                <div className={styles.detailsDiv} style={{backgroundColor: `rgba(0,0,0,0.6)`}}>
                    <a href={'/'} target={'_blank'} rel="noreferrer" className={styles.link}>&nbsp;</a>
                    <span className={styles.ellipsis}></span>
                </div>
            )

            let postWrapperStyles = {
                transform : `translate(${x}px,${y}px)`,
                width : rect.w * gridSize - imagePadding + 'px',
                height : rect.h * gridSize - imagePadding + 'px',
                backgroundColor :  `rgba(${color},1)`
            }

            let postWrapper = (
                <div key={`rect_${rectIndex}`} className={styles.postWrapper} style={postWrapperStyles}
                     ref={(ref) => { posts.current[rectIndex] = ref}}>
                    {deets}
                    <img aria-hidden={true} className={styles.loader} src={'https://msobd-storage.azureedge.net/images/loader-md.gif'} alt={''}/>
                </div>
            )

            // Add image to the list
            newGridWrapper.push(postWrapper);
        })

        setGridWrapper(newGridWrapper);

    },[setGridWrapper,calculateGrid,colorArray, gridMin])

    /** Kick off initialization **/

    useEffect(()=>initializeGrid(),[initializeGrid]);

    /** Load our submitted assets **/

        // Check if a rect intersects the viewport
    const rectIntersectsWithViewport = useCallback((rect) => {
            return (
                rect.x * gridSize + (-container.current.scrollLeft) <= width.current &&
                0 <= (rect.x + rect.w) * gridSize + (-container.current.scrollLeft) &&
                rect.y * gridSize + (-container.current.scrollTop) <= height.current &&
                0 <= (rect.y + rect.h) * gridSize + (-container.current.scrollTop)
            )
        },[gridSize]);

    const loadWithSubmission = useCallback((index, rect, post, submission, loadTextureForImage) => {

        if(submission && (submission.post_image || submission.post_video)){

            let url = submission.post_video_cdn || submission.post_image

            // Fetch the image
            // fetch(url).then(response => {

                const id = url.split('?')[0]
                if (postUrls.current[id]) {
                    loadTextureForImage(index)
                } else {
                    postUrls.current[id] = true;

                    let loader = post.querySelector(`.${styles.loader}`);
                    loader.style.display = 'none';

                    let postContent;

                    if(submission.post_video){
                        postContent = document.createElement('video');
                        postContent.classList.add(styles.video);
                        postContent.muted = true;
                        postContent.autoplay = true;
                        postContent.loop = true;
                        postContent.src = url;
                        postContent.setAttribute('aria-hidden', 'true');
                        post.appendChild(postContent)
                    }

                    else if(submission.post_image){
                        postContent = document.createElement('img');
                        postContent.classList.add(styles.image);
                        postContent.src = url;
                        postContent.setAttribute('aria-hidden', 'true');
                        post.appendChild(postContent)
                    }

                    gsap.fromTo(postContent, {opacity: 0}, {opacity: 1, duration: 1, delay: 0.5})

                    let link = post.querySelector(`.${styles.link}`);
                    link.href = submission.post_link;
                    link.innerText = 'Visit post';

                    let ellipsis = post.querySelector(`.${styles.ellipsis}`);
                    ellipsis.innerHTML = submission.comment;
                    ellipsis.setAttribute('lang', submission.language)

                    rect.loaded = true

                }

            // }).catch((error) => {
            //
            //     console.log('Error:',error);
            //     /** move on and fill with the next submission **/
            //     submission = submissions.current.shift();
            //     loadWithSubmission(index, rect, post, submission)
            //
            //     // Catch errors silently, for not showing the following error message if it is aborted:
            //     // AbortError: The operation was aborted.
            // })

        } else if(submission){
            console.log(submission)
        }

        if(submissions.current.length === 0){

            /** ALl sumbmissions loaded, kill the loaders and the detail overlays for the remaining empty squares **/

            let loaders = post.querySelectorAll(`.${styles.loader}`);
            loaders.forEach((loader)=>{
                loader.style.display = 'none';
                loader.parentNode.firstChild.style.display = 'none';
                loader.parentNode.style.backgroundColor = '#000';
            })

        }

    }, []);

    // Load texture for an image, giving its index
    const loadTextureForImage = useCallback((index) => {

        // Get image Sprite
        const post = posts.current[index];

        // Set the url to an image/video from WallsIO Source
        let submission;

        submission = submissions.current.shift();

        // Get the corresponding rect, to store more data needed (it is a normal Object)
        const rect = rects.current[index];

        /** TODO, if CORS continues on video elements and we extend this into our API to make the fetch,
         * we should also shift the submission and put the following below in a function to re-run with a
         * new submission after a failed one...
         */

        loadWithSubmission(index, rect, post, submission, loadTextureForImage)

    },[loadWithSubmission]);

    const calcPosition = useCallback(() => {

        /** don't drag till content is ready **/
        if(!submissions.current) return;


        // Multiply the values by a coefficient to get a smooth animation
        uniforms.current.uPointerDown += (pointerDownTarget.current - uniforms.current.uPointerDown) * 0.075
        uniforms.current.uPointerDiff.x += (diffX.current - uniforms.current.uPointerDiff.x)
        uniforms.current.uPointerDiff.y += (diffY.current - uniforms.current.uPointerDiff.y)

        // Set position for the container
        container.current.scrollLeft = -(uniforms.current.uPointerDiff.x - centerX.current)
        container.current.scrollTop = -(uniforms.current.uPointerDiff.y - centerY.current)

        // Check if rects intersects with the viewport and loads corresponding image
        // Loop over rects
        rects.current.forEach((rect, index) => {

            // Check if the rect intersects with the viewport
            if (rectIntersectsWithViewport(rect)) {

                // If rect just has been discovered
                // start loading image
                if (!rect.discovered) {
                    rect.discovered = true
                    loadTextureForImage(index)
                }

            }
        })
    },[rectIntersectsWithViewport,loadTextureForImage])


    useEffect(()=>{
        setTimeout(()=>calcPosition(), 500);
    }, [calcPosition])

    /** Initialize container and container events **/

    const onScroll = useCallback((e)=>{

        if(!submissions.current) return

        if(scrollTimeout.current) clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(()=>{
            if(pointerDownTarget.current !== 1){
                let x = centerX.current - container.current.scrollLeft;
                let y = centerY.current - container.current.scrollTop;
                diffX.current = x;
                diffY.current = y;
                uniforms.current.uPointerDiff.x = x;
                uniforms.current.uPointerDiff.y = y;

                calcPosition()
            }
            scrollCoords.current = {x: container.current.scrollLeft, y: container.current.scrollTop};
        },100)


    },[calcPosition])

    const initContainer = useCallback((loadedContainer)=>{

        // always make sure we start centered
        if(loadedContainer){
            loadedContainer.scrollLeft = centerX.current
            loadedContainer.scrollTop = centerY.current
        }

        // set the container and load the event listeners only once
        if(container.current) return;

        container.current = loadedContainer;
        container.current.addEventListener('scroll', onScroll);
        return () => container.current.removeEventListener('scroll', onScroll);

    }, [onScroll])


    /** Initialize APP and container events **/

        // On pointer move, calculate coordinates diff
    const onPointerMove = useCallback((e)=>{

            e.preventDefault();

            const x = e.pageX;
            const y = e.pageY;

            if (pointerDownTarget.current) {
                diffX.current = pointerDiffStart.current.x + (x - pointerStart.current.x)
                diffY.current= pointerDiffStart.current.y + (y - pointerStart.current.y)
                diffX.current = diffX.current > 0 ? Math.min(diffX.current, centerX.current + imagePadding) : Math.max(diffX.current, -(centerX.current + widthRest.current))
                diffY.current = diffY.current > 0 ? Math.min(diffY.current, centerY.current + imagePadding) : Math.max(diffY.current, -(centerY.current + heightRest.current))
            }

        },[])

    // On pointer down, save coordinates and set pointerDownTarget
    const onPointerDown = useCallback((e)=>{

        const x = e.pageX;
        const y = e.pageY;

        pointerDownTarget.current = 1
        pointerStart.current = { x:x, y:y }

        pointerDiffStart.current = { x:uniforms.current.uPointerDiff.x, y:uniforms.current.uPointerDiff.y }
        app.current.style.cursor = 'grabbing';

        /***** Animation loop *****/
            // Code here will be executed on every animation frame
        let animationFrame;

        const animationLoop = () => {
            calcPosition();
            animationFrame = requestAnimationFrame(animationLoop);
        }

        animationLoop()

        const mouseUpHandler = () => {
            app.current.style.cursor = 'grab';

            // putting this after scroll timeout to differentiate between grab scroll and mouse/pad scroll
            setTimeout(()=>{pointerDownTarget.current = 0}, 200);

            cancelAnimationFrame(animationFrame);
            window.removeEventListener('mousemove', onPointerMove);
            window.removeEventListener('mouseup', mouseUpHandler);
            window.removeEventListener('mouseupoutside', mouseUpHandler);
        }

        window.addEventListener('mousemove', onPointerMove);
        window.addEventListener('mouseup', mouseUpHandler);
        window.addEventListener('mouseupoutside', mouseUpHandler);

    },[calcPosition,onPointerMove])


    const initApp = useCallback((newApp)=>{

        if(app.current) return;

        app.current = newApp;

        if(isMobileDevice()) return

        app.current.addEventListener('mousedown', onPointerDown);

        return ()=>{
            app.current.removeEventListener('mousedown', onPointerDown);
        }

    },[onPointerDown])

    return (
        <div ref={(app)=>initApp(app)} className={`${styles.view} app`}>
            <div ref={(container)=>initContainer(container)} className={styles.container}>
                {gridWrapper}
            </div>
        </div>

    );
}

export default ImageGallery
